<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { REPORTS_PERMISSIONS } from 'dashboard/constants/permissions';
import { OnClickOutside } from '@vueuse/components';
import { useMediaQuery } from '@vueuse/core';
import { KANBAN_COLUMN_WIDTH_STYLES, intlLocale } from '../constants';
import { useChannelIcon } from 'dashboard/components-next/icon/provider';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import KanbanBoard from 'kanban/components/KanbanBoard.vue';
import KanbanBoardSwitcher from 'kanban/components/KanbanBoardSwitcher.vue';
import KanbanBoardModal from 'kanban/components/KanbanBoardModal.vue';
import KanbanTaskSortMenu from 'kanban/components/KanbanTaskSortMenu.vue';
import KanbanTaskModal from 'kanban/components/KanbanTaskModal.vue';
import KanbanStepModal from 'kanban/components/KanbanStepModal.vue';
import KanbanDeleteTaskDialog from 'kanban/components/KanbanDeleteTaskDialog.vue';
import KanbanBoardSearch from 'kanban/components/KanbanBoardSearch.vue';
import KanbanDueFilter from 'kanban/components/filters/KanbanDueFilter.vue';
import TaskAttributeFiltersPanel from 'kanban/components/filters/TaskAttributeFiltersPanel.vue';
import { useKanbanFilterOptions } from 'kanban/composables/useKanbanFilterOptions';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import TaskProductsAPI from 'kanban/api/taskProducts';
import TasksAPI from 'kanban/api/tasks';
import kanbanModule from 'kanban/store/modules/kanban';
import types from 'dashboard/store/mutation-types';
import { useBoardModal } from 'kanban/composables/useBoardModal';
import {
  activeFilterCount,
  createFilterState,
  hasFilterQuery,
  stateFromPreferences,
  stateFromQuery,
  stateToApiParams,
  stateToPreferences,
  stateToQuery,
} from 'kanban/helper/taskFilters';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t, locale } = useI18n();
const { isAdmin } = useAdmin();
const { checkPermissions } = usePolicy();
const canViewReports = computed(() =>
  checkPermissions(['administrator', REPORTS_PERMISSIONS])
);
const isLargeScreen = useMediaQuery('(min-width: 1280px)');

const fazerAiSubscription = computed(
  () => store.getters['globalConfig/getFazerAiSubscription']
);
const isFazerAiSubscriptionActive = computed(() => {
  const status = fazerAiSubscription.value?.status;
  return ['active', 'past_due', 'trialing'].includes(status);
});

if (!store.hasModule('kanban')) {
  store.registerModule('kanban', kanbanModule);
}

const preferences = computed(() => store.state.kanban.preferences);
const steps = computed(() => store.getters['kanban/orderedSteps']);
const boards = computed(() => store.state.kanban.boards);
const activeBoard = computed(() => store.getters['kanban/activeBoard']);
// Plain agent (no custom role) on a funnel restricted to own tasks: the
// per-agent filter is meaningless (they only ever see their own cards).
const isBoardRestrictedForViewer = board =>
  !!board?.settings?.restrict_agent_view_to_own_tasks &&
  store.getters.getCurrentRole === 'agent' &&
  !store.getters.getCurrentCustomRoleId;
const isViewerRestricted = computed(() =>
  isBoardRestrictedForViewer(activeBoard.value)
);
const selectedBoardId = computed(() => store.state.kanban.selectedBoardId);
const isLoading = computed(() => store.state.kanban.isLoading);

// Step-based loading state
const stepTasksMap = computed(() => store.getters['kanban/stepTasksMap']);
const stepMetaMap = computed(() => store.getters['kanban/stepMetaMap']);
const stepLoadingMap = computed(() => store.getters['kanban/stepLoadingMap']);
const stepFetchedMap = computed(() => store.getters['kanban/stepFetchedMap']);
// Derive all fetched tasks from stepTasksMap for filter counts
const allFetchedTasks = computed(() => {
  const allTasks = stepTasksMap.value || {};
  return Object.values(allTasks).flat();
});

const activeSort = ref('position');
const activeOrdering = ref('asc');

// Estado unico dos filtros do funil (helper/taskFilters.js): o que vai para a API, para as preferencias e,
// pela pagina, para a URL.
const filters = reactive(createFilterState());

const collapsedStepIds = computed(() => {
  return steps.value
    .filter(step => {
      if (step.inferred_task_status === 'completed' && !filters.showCompleted) {
        return true;
      }
      if (step.inferred_task_status === 'cancelled' && !filters.showCancelled) {
        return true;
      }
      return false;
    })
    .map(step => step.id);
});

const fetchTasksForStep = (stepId, options = {}) =>
  store.dispatch('kanban/fetchTasksForStep', { stepId, ...options });
const fetchMoreTasksForStep = stepId =>
  store.dispatch('kanban/fetchMoreTasksForStep', stepId);

// Fetch tasks for visible (non-collapsed) steps when they become available
const fetchVisibleStepTasks = async () => {
  const visibleSteps = steps.value.filter(
    step => !collapsedStepIds.value.includes(step.id)
  );

  await Promise.all(
    visibleSteps.map(step => {
      if (!stepFetchedMap.value[step.id]) {
        return fetchTasksForStep(step.id, { page: 1, perPage: 10 });
      }
      return Promise.resolve();
    })
  );
};

// O store le os filtros das preferencias salvas (loadBoardFilters grava antes).
const setActiveBoard = id =>
  store.dispatch('kanban/setActiveBoard', { boardId: id });
const fetchBoards = () => store.dispatch('kanban/fetchBoards');
const createTask = data => store.dispatch('kanban/createTask', data);
const updateTask = data => store.dispatch('kanban/updateTask', data);

// Etiquetas sao da conta; produtos sao do funil ativo (ja vem no payload do board).
const accountLabels = useMapGetter('labels/getLabels');
const boardProducts = useMapGetter('kanban/getProducts');
// Atributos personalizados de cartao: base do painel "Filtros" e da validacao dos filtros salvos/da URL.
const attributesByModel = useMapGetter('attributes/getAttributesByModel');
const taskAttributeDefinitions = computed(() =>
  attributesByModel.value('task_attribute')
);
const showAttributeFilters = ref(false);
const attributeFilterCount = computed(() => activeFilterCount(filters));
const hasActiveFilters = computed(
  () => Object.keys(stateToApiParams(filters)).length > 0
);
// Mostrar ganhos/perdidos e visualizacao, nao filtro: continua como esta.
const clearFilters = () => {
  Object.assign(
    filters,
    createFilterState({
      showCompleted: filters.showCompleted,
      showCancelled: filters.showCancelled,
    })
  );
};

// A URL espelha os filtros (mesmos nomes da API), para o link reproduzir o funil filtrado.
const syncFiltersToUrl = state => {
  const query = stateToQuery(state);
  if (JSON.stringify(query) === JSON.stringify(route.query || {})) return;
  router.replace({ name: route.name, params: route.params, query });
};

const isCountsLoading = ref(false);

// If restriction flips on while the page is open (e.g. admin toggles it and the
// board update arrives over realtime), the hidden agent filter could stay active
// and unchangeable; force it back to 'all'.
watch(
  isViewerRestricted,
  restricted => {
    if (restricted && filters.agentId !== 'all') {
      filters.agentId = 'all';
    }
  },
  { immediate: true }
);

const fetchStepsWithFilters = async () => {
  isCountsLoading.value = true;
  try {
    await store.dispatch('kanban/fetchSteps', {
      boardId: selectedBoardId.value,
    });
  } finally {
    isCountsLoading.value = false;
  }
};

const moveTask = ({ task, destinationStepId, insertBeforeTaskId }) =>
  store.dispatch('kanban/moveTask', {
    taskId: task.id,
    destinationStepId,
    insertBeforeTaskId,
    refreshStepId: destinationStepId,
  });
const removeTask = id => store.dispatch('kanban/deleteTask', id);
const createStep = data => store.dispatch('kanban/createStep', data);
const updateStep = data => store.dispatch('kanban/updateStep', data);
const removeStep = data => store.dispatch('kanban/deleteStep', data);

const hasSteps = computed(() => steps.value.length > 0);
const isMissingCompletedStep = computed(
  () => steps.value.length > 1 && !steps.value.some(step => step.completed)
);
const showBoardSwitcher = ref(false);
const showTaskModal = ref(false);
const showStepModal = ref(false);
const selectedTask = ref(null);
const duplicateTask = ref(null);
const selectedStep = ref(null);
const selectedStepId = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const isSavingStep = ref(false);
const isDeletingStep = ref(false);
const showDeleteDialog = ref(false);
const taskToDelete = ref(null);
const isDataLoaded = ref(false);

const onSortChange = async ({ sort, order }) => {
  activeSort.value = sort;
  activeOrdering.value = order;

  // Save sort preferences
  await store.dispatch('kanban/updateTaskSorting', {
    boardId: selectedBoardId.value,
    sort,
    order,
  });

  // Reset step tasks and re-fetch with new sort
  await store.dispatch('kanban/resetStepTasks');
  await fetchVisibleStepTasks();
};

const isDragEnabled = computed(() => activeSort.value === 'position');

const agents = computed(() => activeBoard.value?.assigned_agents || []);
const inboxes = computed(() => activeBoard.value?.assigned_inboxes || []);
const currentUserId = computed(() => store.getters.getCurrentUserID);

const agentOptions = computed(() => {
  const allOption = {
    label: t('KANBAN.FILTERS.ALL_AGENTS'),
    value: 'all',
    icon: 'i-lucide-user',
  };
  const sortedAgents = [...agents.value].sort((a, b) => {
    if (a.id === currentUserId.value) return -1;
    if (b.id === currentUserId.value) return 1;
    return 0;
  });

  const options = sortedAgents.map(agent => {
    const isCurrentUser = agent.id === currentUserId.value;
    const label = isCurrentUser
      ? `${agent.name} (${t('KANBAN.FILTERS.ME')})`
      : agent.name;

    return {
      label,
      value: String(agent.id),
      thumbnail: agent.avatar_url,
    };
  });
  const result = [allOption, ...options];

  if (isAdmin.value) {
    result.push({
      label: t('KANBAN.FILTERS.ASSIGN_AGENT', 'Assign agent'),
      value: 'assign_agent',
      icon: 'i-lucide-plus',
    });
  }

  return result;
});

const inboxOptions = computed(() => {
  const allOption = {
    label: t('KANBAN.FILTERS.ALL_INBOXES'),
    value: 'all',
    icon: 'i-lucide-inbox',
  };

  const options = inboxes.value.map(inbox => {
    return {
      label: inbox.name,
      value: String(inbox.id),
      icon: useChannelIcon(inbox).value,
    };
  });
  const result = [allOption, ...options];

  if (isAdmin.value) {
    result.push({
      label: t('KANBAN.FILTERS.ASSIGN_INBOX', 'Assign inbox'),
      value: 'assign_inbox',
      icon: 'i-lucide-plus',
    });
  }

  return result;
});

// Etiquetas da conta, origens configuradas no funil e produtos do funil: os tres eixos de
// negocio que o relatorio usa, agora tambem como filtro do quadro.
const { labelOptions, sourceOptions, productOptions } = useKanbanFilterOptions({
  boards: computed(() => (activeBoard.value ? [activeBoard.value] : [])),
  products: boardProducts,
});

const dateFieldOptions = computed(() => [
  {
    label: t('KANBAN.FILTERS.CREATED_AT'),
    value: 'created_at',
    icon: 'i-lucide-calendar-plus',
  },
  {
    label: t('KANBAN.FILTERS.EXPECTED_CLOSE_DATE'),
    value: 'expected_close_date',
    icon: 'i-lucide-calendar-check-2',
  },
]);

const periodOptions = computed(() => [
  {
    label: t('KANBAN.FILTERS.ALL_PERIODS'),
    value: 'all',
    icon: 'i-lucide-calendar',
  },
  {
    label: t('KANBAN.FILTERS.LAST_MONTH'),
    value: 'last_month',
    icon: 'i-lucide-calendar-arrow-left',
  },
  {
    label: t('KANBAN.FILTERS.CURRENT_MONTH'),
    value: 'current_month',
    icon: 'i-lucide-calendar-days',
  },
  {
    label: t('KANBAN.FILTERS.NEXT_MONTH'),
    value: 'next_month',
    icon: 'i-lucide-calendar-arrow-right',
  },
]);

const filteredTasksByStep = computed(() => {
  const allTasks = stepTasksMap.value || {};
  const filtered = {};
  Object.keys(allTasks).forEach(stepId => {
    filtered[stepId] = (allTasks[stepId] || []).filter(task => {
      if (filters.agentId !== 'all') {
        const hasAgent = task.assigned_agents.some(
          a => String(a.id) === filters.agentId
        );
        if (!hasAgent) return false;
      }

      if (filters.inboxId !== 'all') {
        const hasInbox = task.conversations.some(
          c => String(c.inbox.id) === filters.inboxId
        );
        if (!hasInbox) return false;
      }

      if (task.status === 'completed' && !filters.showCompleted) return false;
      if (task.status === 'cancelled' && !filters.showCancelled) return false;

      return true;
    });
  });
  return filtered;
});

const filteredTotalTasks = computed(() => {
  // Use filtered count from API when available, otherwise use tasks_count
  return steps.value
    .filter(step => !collapsedStepIds.value.includes(step.id))
    .reduce(
      (sum, step) => sum + (step.filtered_tasks_count ?? step.tasks_count ?? 0),
      0
    );
});

const boardCurrency = computed(() => activeBoard.value?.currency || 'USD');

const totalWeightedValue = computed(() => {
  return steps.value.reduce((sum, step) => {
    return sum + (Number(step.weighted_value_sum) || 0);
  }, 0);
});

const totalFullValue = computed(() => {
  return steps.value.reduce((sum, step) => {
    return sum + (Number(step.value_sum) || 0);
  }, 0);
});

const formattedWeightedValue = computed(() => {
  if (!totalWeightedValue.value || totalWeightedValue.value <= 0) return '';
  return new Intl.NumberFormat(intlLocale(locale.value), {
    style: 'currency',
    currency: boardCurrency.value,
    notation: 'compact',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalWeightedValue.value);
});

const formattedFullValue = computed(() => {
  if (!totalFullValue.value || totalFullValue.value <= 0) return '';
  return new Intl.NumberFormat(intlLocale(locale.value), {
    style: 'currency',
    currency: boardCurrency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalFullValue.value);
});

const formattedFullWeightedValue = computed(() => {
  if (!totalWeightedValue.value || totalWeightedValue.value <= 0) return '';
  return new Intl.NumberFormat(intlLocale(locale.value), {
    style: 'currency',
    currency: boardCurrency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalWeightedValue.value);
});

// Handle loading more tasks for a step
const onLoadMore = stepId => {
  fetchMoreTasksForStep(stepId);
};

// Handle step expansion - fetch tasks if not yet fetched
const onExpandStep = stepId => {
  if (!stepFetchedMap.value[stepId]) {
    fetchTasksForStep(stepId, { page: 1, perPage: 10 });
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  // Etiquetas e produtos alimentam os filtros de negocio do quadro.
  if (!accountLabels.value?.length) store.dispatch('labels/get');
  // Reset so watcher doesn't fire until syncBoardFromRoute completes
  isDataLoaded.value = false;
});

// Watch for steps to be loaded and fetch visible step tasks
watch(
  () => steps.value,
  async newSteps => {
    if (isDataLoaded.value && newSteps.length > 0) {
      await fetchVisibleStepTasks();
    }
  }
);

// When collapsed steps change (user shows completed/cancelled), fetch newly visible steps
watch(
  () => [filters.showCompleted, filters.showCancelled],
  async () => {
    if (isDataLoaded.value && steps.value.length > 0) {
      await fetchVisibleStepTasks();
    }
  }
);

watch(
  selectedBoardId,
  newBoardId => {
    if (newBoardId) {
      store.dispatch('kanban/fetchProducts', { boardId: newBoardId });
      const savedSort = preferences.value.task_sorting?.[newBoardId] || {};
      activeSort.value = savedSort.sort || 'position';
      activeOrdering.value = savedSort.order || 'asc';

      // Reset step tasks when switching boards
      store.dispatch('kanban/resetStepTasks');
    }
  },
  { immediate: true }
);

watch(agents, () => {
  if (filters.agentId === 'all') return;
  const agentExists = agents.value.some(
    agent => String(agent.id) === filters.agentId
  );
  if (!agentExists) {
    filters.agentId = 'all';
  }
});

watch(inboxes, () => {
  if (filters.inboxId === 'all') return;
  const inboxExists = inboxes.value.some(
    inbox => String(inbox.id) === filters.inboxId
  );
  if (!inboxExists) {
    filters.inboxId = 'all';
  }
});

// O store busca com a busca de texto atual; precisa estar la antes de o watcher abaixo recarregar os cartoes.
watch(
  () => filters.search,
  query => store.dispatch('kanban/setSearchQuery', query),
  { flush: 'sync', immediate: true }
);

const apiFiltersKey = state => JSON.stringify(stateToApiParams(state));

// Toda mudanca de filtro e salva; so o que vai para a API recarrega colunas e cartoes (mostrar ganhos/perdidos e
// local).
watch(
  () => ({ ...filters }),
  async (current, previous) => {
    if (!selectedBoardId.value || !isDataLoaded.value) return;

    store.dispatch('kanban/updateBoardFilters', {
      boardId: selectedBoardId.value,
      filters: current,
    });
    syncFiltersToUrl(current);

    if (apiFiltersKey(current) === apiFiltersKey(previous)) return;
    // Reset tasks first so skeletons appear immediately
    await store.dispatch('kanban/resetStepTasks');
    // Then fetch steps (for counts) and tasks
    await fetchStepsWithFilters();
    await fetchVisibleStepTasks();
  },
  { deep: true }
);

const activeBoardName = computed(
  () => activeBoard.value?.name || t('KANBAN.BOARDS.FALLBACK_NAME')
);

const closeBoardSwitcher = () => {
  showBoardSwitcher.value = false;
};

const toggleBoardSwitcher = () => {
  showBoardSwitcher.value = !showBoardSwitcher.value;
};

// Ao carregar um funil, filtros na URL vencem os salvos; sem filtros na URL, valem os salvos. O store busca a
// partir das preferencias, entao grava antes quando o estado difere. Agente restrito nunca restaura o filtro de
// agente escondido (cairia num funil vazio sem como limpar).
const loadBoardFilters = boardId => {
  const saved = preferences.value.board_filters?.[boardId];
  const definitions = taskAttributeDefinitions.value;
  const { state, dropped } = hasFilterQuery(route.query)
    ? stateFromQuery(route.query, definitions)
    : stateFromPreferences(saved, definitions);
  const targetBoard = boards.value.find(b => b.id === boardId);
  if (isBoardRestrictedForViewer(targetBoard)) state.agentId = 'all';
  Object.assign(filters, state);
  if (dropped > 0) useAlert(t('KANBAN.FILTERS.INVALID_FILTERS_DROPPED'));
  syncFiltersToUrl(state);

  const savedState = stateFromPreferences(saved).state;
  if (
    JSON.stringify(stateToPreferences(state)) !==
    JSON.stringify(stateToPreferences(savedState))
  ) {
    store.dispatch('kanban/updateBoardFilters', { boardId, filters: state });
  }
};

const navigateToBoard = boardId => {
  if (!boardId || !route.params.accountId) return;
  const id = Number(boardId);

  if (Number(route.params.boardId) !== id) {
    router.replace({
      name: 'kanban_board_show',
      params: {
        accountId: route.params.accountId,
        boardId: id,
      },
    });
  } else if (selectedBoardId.value !== id) {
    loadBoardFilters(id);
    setActiveBoard(id);
  }

  closeBoardSwitcher();
};

const openCreateModal = (stepId = null) => {
  const validStepId =
    typeof stepId === 'number' || typeof stepId === 'string' ? stepId : null;

  selectedTask.value = null;
  duplicateTask.value = null;
  selectedStepId.value = validStepId || steps.value[0]?.id;

  if (route.name !== 'kanban_task_create') {
    router.push({
      name: 'kanban_task_create',
      params: {
        accountId: route.params.accountId,
        boardId: route.params.boardId,
      },
      query: route.query,
    });
  }

  showTaskModal.value = true;
};

const openEditModal = task => {
  selectedTask.value = task;
  duplicateTask.value = null;
  selectedStepId.value = task.board_step_id;

  if (
    route.name !== 'kanban_task_show' ||
    Number(route.params.taskId) !== task.id
  ) {
    router.push({
      name: 'kanban_task_show',
      params: {
        accountId: route.params.accountId,
        boardId: route.params.boardId,
        taskId: task.id,
      },
      query: route.query,
    });
  }

  showTaskModal.value = true;
};

const openDuplicateModal = task => {
  selectedTask.value = null;
  duplicateTask.value = task;
  selectedStepId.value = task.board_step_id;

  if (route.name !== 'kanban_task_create') {
    router.push({
      name: 'kanban_task_create',
      params: {
        accountId: route.params.accountId,
        boardId: route.params.boardId,
      },
      query: route.query,
    });
  }

  showTaskModal.value = true;
};

const openDeleteDialog = task => {
  taskToDelete.value = task;
  showDeleteDialog.value = true;
};

const closeDeleteDialog = () => {
  showDeleteDialog.value = false;
  taskToDelete.value = null;
};

const confirmDeleteTask = async () => {
  if (!taskToDelete.value) return;

  isDeleting.value = true;
  try {
    await removeTask(taskToDelete.value.id);
    closeDeleteDialog();
  } catch {
    // Handle error
  } finally {
    isDeleting.value = false;
  }
};

const openCreateStepModal = () => {
  selectedStep.value = null;
  showStepModal.value = true;
};

const openStepModal = step => {
  selectedStep.value = step;
  showStepModal.value = true;
};

const closeModal = () => {
  showTaskModal.value = false;
  selectedTask.value = null;
  duplicateTask.value = null;
  selectedStepId.value = null;

  if (route.name !== 'kanban_board_show') {
    router.push({
      name: 'kanban_board_show',
      params: {
        accountId: route.params.accountId,
        boardId: route.params.boardId,
      },
      query: route.query,
    });
  }
};

const closeStepModal = () => {
  showStepModal.value = false;
  selectedStep.value = null;
};

const syncBoardFromRoute = async () => {
  const boardId = route.params.boardId ? Number(route.params.boardId) : null;
  const hasMatchingBoard = boards.value.some(board => board.id === boardId);

  if (boardId && hasMatchingBoard) {
    // Only fetch data if we're switching to a different board or data isn't loaded
    const needsDataLoad =
      selectedBoardId.value !== boardId || !isDataLoaded.value;
    if (needsDataLoad) {
      isDataLoaded.value = false;

      loadBoardFilters(boardId);
      await setActiveBoard(boardId);
      isDataLoaded.value = true;
      await fetchVisibleStepTasks();
    }

    const taskId = route.params.taskId ? Number(route.params.taskId) : null;
    if (route.name === 'kanban_task_create') {
      if (!showTaskModal.value) {
        openCreateModal();
      }
    } else if (taskId && route.name === 'kanban_task_show') {
      const task = allFetchedTasks.value.find(tt => tt.id === taskId);
      if (task) {
        if (!showTaskModal.value || selectedTask.value?.id !== taskId) {
          selectedTask.value = task;
          duplicateTask.value = null;
          selectedStepId.value = task.board_step_id;
          showTaskModal.value = true;
        }
      } else {
        router.replace({
          name: 'kanban_board_show',
          params: {
            accountId: route.params.accountId,
            boardId: route.params.boardId,
          },
          query: route.query,
        });
      }
    }
    return;
  }

  router.replace({
    name: 'kanban_list',
    params: { accountId: route.params.accountId },
  });
};

onMounted(async () => {
  if (!isFazerAiSubscriptionActive.value) {
    router.replace({
      name: 'kanban_list',
      params: { accountId: route.params.accountId },
    });
    return;
  }

  await Promise.all([
    fetchBoards(),
    taskAttributeDefinitions.value.length
      ? null
      : store.dispatch('attributes/get'),
  ]);
  await syncBoardFromRoute();
});

const syncTaskProducts = async (taskId, lineItems, originalProducts) => {
  const originalIds = new Set((originalProducts || []).map(p => p.id));
  const currentIds = new Set(lineItems.filter(li => li.id).map(li => li.id));

  // Delete removed items
  const toDelete = [...originalIds].filter(id => !currentIds.has(id));
  await Promise.all(toDelete.map(id => TaskProductsAPI.delete(taskId, id)));

  // Create new items (no id, has tempId)
  const toCreate = lineItems.filter(li => !li.id);
  await Promise.all(
    toCreate.map(li =>
      TaskProductsAPI.create(taskId, {
        product_id: li.product_id,
        quantity: li.quantity,
        unit_price: li.unit_price,
        discount_percentage: li.discount_percentage,
      })
    )
  );

  // Update existing items that changed
  const toUpdate = lineItems.filter(li => li.id && originalIds.has(li.id));
  const originalMap = Object.fromEntries(
    (originalProducts || []).map(p => [p.id, p])
  );
  await Promise.all(
    toUpdate
      .filter(li => {
        const orig = originalMap[li.id];
        return (
          !orig ||
          Number(li.quantity) !== Number(orig.quantity) ||
          Number(li.unit_price) !== Number(orig.unit_price) ||
          Number(li.discount_percentage) !== Number(orig.discount_percentage)
        );
      })
      .map(li =>
        TaskProductsAPI.update(taskId, li.id, {
          quantity: li.quantity,
          unit_price: li.unit_price,
          discount_percentage: li.discount_percentage,
        })
      )
  );
};

const saveTask = async data => {
  isSaving.value = true;
  const { taskLineItems, ...taskData } = data;

  try {
    let savedTask;
    if (taskData.task.id) {
      savedTask = await updateTask({
        id: taskData.task.id,
        task: taskData.task,
      });
    } else {
      savedTask = await createTask(taskData);
    }

    if (taskLineItems && savedTask?.id) {
      const originalProducts = taskData.task.id
        ? selectedTask.value?.task_products
        : [];
      await syncTaskProducts(savedTask.id, taskLineItems, originalProducts);
    }

    // Refetch to get canonical state and update both stores
    if (savedTask?.id) {
      const { data: refreshed } = await TasksAPI.show(savedTask.id);
      store.commit('kanban/UPDATE_TASK', refreshed);
      (refreshed.conversations || []).forEach(conv => {
        store.commit(types.UPDATE_CONVERSATION_KANBAN_TASK, {
          task: refreshed,
          conversationId: conv.id,
        });
      });
    }

    closeModal();
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('KANBAN.MODAL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveStep = async data => {
  isSavingStep.value = true;
  try {
    if (data.id) {
      await updateStep({
        boardId: selectedBoardId.value,
        stepId: data.id,
        step: data,
      });
    } else {
      await createStep({
        boardId: selectedBoardId.value,
        step: data,
      });
    }
    closeStepModal();
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('KANBAN.MODAL.SAVE_ERROR'));
  } finally {
    isSavingStep.value = false;
  }
};

const deleteStep = async id => {
  isDeletingStep.value = true;
  try {
    await removeStep({
      boardId: selectedBoardId.value,
      stepId: id,
    });
    closeStepModal();
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('KANBAN.MODAL.DELETE_ERROR'));
  } finally {
    isDeletingStep.value = false;
  }
};

const deleteTask = async id => {
  isDeleting.value = true;
  try {
    await removeTask(id);
    closeModal();
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t('KANBAN.MODAL.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

const navigateToSettings = (hash = '') => {
  if (!selectedBoardId.value || !route.params.accountId) return;
  router.push({
    name: 'kanban_board_settings',
    params: {
      accountId: route.params.accountId,
      boardId: selectedBoardId.value,
    },
    hash,
  });
};

const handleAgentSelect = value => {
  if (value === 'assign_agent') {
    navigateToSettings('#board-agents');
    return;
  }
  filters.agentId = value;
};

const handleInboxSelect = value => {
  if (value === 'assign_inbox') {
    navigateToSettings('#board-inboxes');
    return;
  }
  filters.inboxId = value;
};

const onEnableFilter = status => {
  if (status === 'completed') {
    filters.showCompleted = true;
  } else if (status === 'cancelled') {
    filters.showCancelled = true;
  }
};

const {
  showBoardModal,
  isSavingBoard,
  openBoardModal,
  closeBoardModal,
  saveBoard,
} = useBoardModal({
  onSuccess: newBoard => {
    if (newBoard?.id) {
      navigateToBoard(newBoard.id);
    }
  },
  onError: error => {
    useAlert(
      parseAPIErrorResponse(error) || t('KANBAN.BOARD_MODAL.CREATE_ERROR')
    );
  },
});

watch(
  // Fontes separadas: o Vue compara valor a valor, entao mudar so a query (filtros na URL) nao recarrega o funil.
  [() => route.params.boardId, () => route.params.taskId, () => route.name],
  async () => {
    await syncBoardFromRoute();
  },
  { immediate: false }
);

watch(
  () => boards.value.length,
  async () => {
    if (!isDataLoaded.value) return;
    closeBoardSwitcher();
    await syncBoardFromRoute();
  }
);

const isStepDeletable = computed(() => {
  if (!selectedStep.value) return true;
  return steps.value.length > 1;
});

const skeletonColumnStyle = {
  ...KANBAN_COLUMN_WIDTH_STYLES,
};

const selectedAgentLabel = computed(() => {
  const selected = agentOptions.value.find(o => o.value === filters.agentId);
  return selected ? selected.label : t('KANBAN.FILTERS.ALL_AGENTS');
});

const selectedInboxLabel = computed(() => {
  const selected = inboxOptions.value.find(o => o.value === filters.inboxId);
  return selected ? selected.label : t('KANBAN.FILTERS.ALL_INBOXES');
});

const selectedAgentThumbnail = computed(() => {
  const selected = agentOptions.value.find(o => o.value === filters.agentId);
  return selected ? selected.thumbnail : null;
});

const selectedAgentIcon = computed(() => {
  const selected = agentOptions.value.find(o => o.value === filters.agentId);
  return selected ? selected.icon : null;
});

const selectedInboxIcon = computed(() => {
  const selected = inboxOptions.value.find(o => o.value === filters.inboxId);
  return selected ? selected.icon : null;
});

const selectedDateFieldLabel = computed(() => {
  const selected = dateFieldOptions.value.find(
    option => option.value === filters.dateField
  );
  return selected?.label || t('KANBAN.FILTERS.CREATED_AT');
});

const selectedPeriodLabel = computed(() => {
  const selected = periodOptions.value.find(
    option => option.value === filters.period
  );
  return selected?.label || t('KANBAN.FILTERS.ALL_PERIODS');
});

const selectedLabelLabel = computed(() => {
  const selected = labelOptions.value.find(
    option => option.value === filters.label
  );
  return selected?.label || t('KANBAN.FILTERS.ALL_LABELS');
});

const selectedSourceLabel = computed(() => {
  const selected = sourceOptions.value.find(
    option => option.value === filters.source
  );
  return selected?.label || t('KANBAN.FILTERS.ALL_SOURCES');
});

const selectedProductLabel = computed(() => {
  const selected = productOptions.value.find(
    option => option.value === filters.productId
  );
  return selected?.label || t('KANBAN.FILTERS.ALL_PRODUCTS');
});
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background font-inter">
    <div class="w-full flex justify-center px-3 pt-4 pb-4 sm:px-6 sm:pt-8">
      <div class="w-full max-w-7xl">
        <SettingsLayout :is-loading="false">
          <template #header>
            <div
              class="flex flex-wrap items-center justify-between w-full gap-x-4 gap-y-2"
            >
              <div
                class="flex flex-wrap items-center gap-2 min-w-0 max-w-full shrink"
              >
                <div class="relative min-w-0">
                  <OnClickOutside @trigger="closeBoardSwitcher">
                    <button
                      class="flex h-9 min-w-0 items-center gap-2 rounded-[9px] px-2 transition-colors hover:bg-n-slate-3"
                      @click="toggleBoardSwitcher"
                    >
                      <span
                        class="flex size-6 flex-shrink-0 items-center justify-center rounded-[7px] bg-n-blue-3"
                      >
                        <span
                          class="i-lucide-columns-3 size-3.5 text-n-blue-9"
                        />
                      </span>
                      <span
                        class="text-[15px] font-semibold tracking-[-0.02em] text-n-slate-12 truncate max-w-[220px]"
                      >
                        {{ activeBoardName }}
                      </span>
                      <span
                        class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10"
                      />
                    </button>

                    <KanbanBoardSwitcher
                      v-if="showBoardSwitcher"
                      class="absolute top-10"
                      :boards="boards"
                      :active-board-id="selectedBoardId"
                      @create-board="openBoardModal"
                      @close="closeBoardSwitcher"
                    />
                  </OnClickOutside>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <span
                    v-if="!isLoading"
                    class="flex h-6 min-w-[1.5rem] items-center justify-center rounded-[7px] bg-n-slate-3 px-2 text-xs font-medium text-n-slate-11"
                  >
                    <span
                      v-if="isCountsLoading"
                      class="w-3 h-3 rounded-full bg-n-slate-5 animate-pulse"
                    />
                    <template v-else>{{ filteredTotalTasks }}</template>
                  </span>
                  <span
                    v-if="formattedWeightedValue && !isLoading"
                    v-tooltip="
                      t('KANBAN.BOARD_VALUE_TOOLTIP', {
                        weighted: formattedFullWeightedValue,
                        total: formattedFullValue,
                      })
                    "
                    class="flex h-6 items-center gap-1 rounded-[7px] bg-n-slate-3 px-2 text-xs font-semibold text-n-slate-12 cursor-help"
                  >
                    <i class="i-lucide-banknote w-3 h-3" />
                    {{ formattedWeightedValue }}
                  </span>
                </div>
              </div>
              <div
                class="flex w-full flex-wrap items-center justify-start gap-2 sm:ml-auto sm:w-auto sm:justify-end"
              >
                <Button
                  v-tooltip="
                    filters.showCompleted
                      ? t('KANBAN.FILTERS.HIDE_STATUS', {
                          status: t('KANBAN.STATUS.COMPLETED'),
                        })
                      : t('KANBAN.FILTERS.SHOW_STATUS', {
                          status: t('KANBAN.STATUS.COMPLETED'),
                        })
                  "
                  variant="ghost"
                  :color="filters.showCompleted ? 'teal' : 'slate'"
                  size="sm"
                  icon="i-lucide-check-circle-2"
                  @click="filters.showCompleted = !filters.showCompleted"
                />
                <Button
                  v-tooltip="
                    filters.showCancelled
                      ? t('KANBAN.FILTERS.HIDE_STATUS', {
                          status: t('KANBAN.STATUS.CANCELLED'),
                        })
                      : t('KANBAN.FILTERS.SHOW_STATUS', {
                          status: t('KANBAN.STATUS.CANCELLED'),
                        })
                  "
                  variant="ghost"
                  :color="filters.showCancelled ? 'ruby' : 'slate'"
                  size="sm"
                  icon="i-lucide-x-circle"
                  @click="filters.showCancelled = !filters.showCancelled"
                />
                <div class="w-px h-4 bg-n-slate-4 mx-1" />
                <KanbanBoardSearch v-model="filters.search" />
                <SelectMenu
                  v-if="!isViewerRestricted"
                  class="shrink-0"
                  :model-value="filters.agentId"
                  :options="agentOptions"
                  :label="selectedAgentLabel"
                  :hide-label="!isLargeScreen"
                  :thumbnail="selectedAgentThumbnail"
                  :icon="selectedAgentIcon"
                  show-avatar
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="handleAgentSelect"
                />
                <SelectMenu
                  class="shrink-0"
                  :model-value="filters.inboxId"
                  :options="inboxOptions"
                  :label="selectedInboxLabel"
                  :hide-label="!isLargeScreen"
                  :icon="selectedInboxIcon"
                  show-avatar
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="handleInboxSelect"
                />
                <SelectMenu
                  class="shrink-0"
                  :model-value="filters.dateField"
                  :options="dateFieldOptions"
                  :label="selectedDateFieldLabel"
                  :hide-label="!isLargeScreen"
                  icon="i-lucide-calendar-search"
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="filters.dateField = $event"
                />
                <SelectMenu
                  class="shrink-0"
                  :model-value="filters.period"
                  :options="periodOptions"
                  :label="selectedPeriodLabel"
                  :hide-label="!isLargeScreen"
                  icon="i-lucide-calendar-range"
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="filters.period = $event"
                />
                <KanbanDueFilter
                  v-model:due-status="filters.dueStatus"
                  v-model:due-from="filters.dueFrom"
                  v-model:due-to="filters.dueTo"
                  :hide-label="!isLargeScreen"
                />
                <SelectMenu
                  v-if="labelOptions.length > 1"
                  class="shrink-0"
                  :model-value="filters.label"
                  :options="labelOptions"
                  :label="selectedLabelLabel"
                  :hide-label="!isLargeScreen"
                  icon="i-lucide-tag"
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="filters.label = $event"
                />
                <SelectMenu
                  v-if="sourceOptions.length > 1"
                  class="shrink-0"
                  :model-value="filters.source"
                  :options="sourceOptions"
                  :label="selectedSourceLabel"
                  :hide-label="!isLargeScreen"
                  icon="i-lucide-radio"
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="filters.source = $event"
                />
                <SelectMenu
                  v-if="productOptions.length > 1"
                  class="shrink-0"
                  :model-value="filters.productId"
                  :options="productOptions"
                  :label="selectedProductLabel"
                  :hide-label="!isLargeScreen"
                  icon="i-lucide-package"
                  sub-menu-position="bottom"
                  max-width="max-w-56"
                  @update:model-value="filters.productId = $event"
                />
                <KanbanTaskSortMenu
                  :active-sort="activeSort"
                  :active-ordering="activeOrdering"
                  @update:sort="onSortChange"
                />
                <Button
                  v-if="hasActiveFilters"
                  :label="t('KANBAN.FILTERS.CLEAR_ALL')"
                  icon="i-lucide-filter-x"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  :hide-label="!isLargeScreen"
                  @click="clearFilters"
                />
                <div class="relative">
                  <OnClickOutside @trigger="showAttributeFilters = false">
                    <Button
                      v-tooltip="t('KANBAN.FILTERS.ATTRIBUTES.BUTTON')"
                      icon="i-lucide-filter"
                      variant="ghost"
                      :color="attributeFilterCount ? 'blue' : 'slate'"
                      size="sm"
                      :label="
                        attributeFilterCount
                          ? String(attributeFilterCount)
                          : undefined
                      "
                      @click="showAttributeFilters = !showAttributeFilters"
                    />
                    <TaskAttributeFiltersPanel
                      v-if="showAttributeFilters"
                      v-model="filters.attributeFilters"
                      class="absolute end-0 z-50 top-10"
                      :definitions="taskAttributeDefinitions"
                      @close="showAttributeFilters = false"
                    />
                  </OnClickOutside>
                </div>
                <router-link
                  v-if="canViewReports"
                  :to="{
                    name: 'kanban_reports',
                    params: { accountId: route.params.accountId },
                    query: selectedBoardId
                      ? { board_id: String(selectedBoardId) }
                      : {},
                  }"
                  class="flex items-center"
                >
                  <Button
                    v-tooltip="t('KANBAN.REPORTS.NAV_LABEL')"
                    icon="i-lucide-bar-chart-3"
                    variant="ghost"
                    color="slate"
                    size="sm"
                    class="h-9 w-9 rounded-[10px] border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-11 hover:bg-n-slate-2"
                  />
                </router-link>
                <router-link
                  v-if="selectedBoardId && isAdmin"
                  :to="{
                    name: 'kanban_board_settings',
                    params: {
                      accountId: route.params.accountId,
                      boardId: selectedBoardId,
                    },
                  }"
                  class="flex items-center"
                >
                  <Button
                    icon="i-lucide-settings"
                    variant="ghost"
                    color="slate"
                    size="sm"
                    class="h-9 w-9 rounded-[10px] border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-11 hover:bg-n-slate-2"
                  />
                </router-link>
                <Button
                  :disabled="!hasSteps"
                  icon="i-lucide-plus"
                  size="sm"
                  :label="t('KANBAN.ADD_TASK')"
                  :hide-label="!isLargeScreen"
                  class="h-9 rounded-[10px] font-semibold !bg-n-blue-9 hover:enabled:!bg-n-blue-10 hover:enabled:!brightness-100"
                  @click="openCreateModal"
                />
              </div>
            </div>
          </template>
        </SettingsLayout>
      </div>
    </div>
    <div
      v-if="isMissingCompletedStep && isAdmin && !isLoading"
      class="w-full flex justify-center px-6 pb-3"
    >
      <div
        class="w-full max-w-7xl flex items-center gap-3 rounded-lg border border-n-amber-6 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-12"
      >
        <i
          class="i-lucide-alert-triangle w-5 h-5 flex-shrink-0 text-n-amber-11"
        />
        <span class="flex-1">
          {{ t('KANBAN.WARNINGS.MISSING_COMPLETED_STEP') }}
        </span>
        <router-link
          :to="{
            name: 'kanban_board_settings',
            params: {
              accountId: route.params.accountId,
              boardId: selectedBoardId,
            },
            hash: '#board-steps',
          }"
          class="text-sm font-medium text-n-amber-12 underline hover:no-underline whitespace-nowrap"
        >
          {{ t('KANBAN.WARNINGS.MISSING_COMPLETED_STEP_LINK') }}
        </router-link>
      </div>
    </div>
    <div class="flex-1 overflow-x-auto scrollbar-custom">
      <div class="flex justify-start px-6 min-w-full h-full">
        <div class="w-full h-full">
          <div
            v-if="isLoading"
            class="flex h-full w-full gap-4 px-1 pb-4 animate-pulse overflow-hidden"
          >
            <div
              v-for="(taskCount, index) in [3, 2, 5, 1, 0]"
              :key="index"
              class="flex h-full flex-shrink-0 flex-col overflow-hidden rounded-[14px] bg-n-slate-3 dark:bg-n-solid-2"
              :style="skeletonColumnStyle"
            >
              <div class="flex items-center justify-between px-3.5 py-3.5">
                <div class="flex items-center gap-2">
                  <div class="size-2.5 rounded-full bg-n-slate-5" />
                  <div class="h-5 w-24 bg-n-slate-5 rounded" />
                  <div class="h-5 w-6 bg-n-slate-5 rounded-lg" />
                </div>
                <div class="h-6 w-6 bg-n-slate-5 rounded-md" />
              </div>

              <div class="flex-1 px-2.5 pb-2.5 flex flex-col gap-2">
                <div
                  v-for="taskIndex in taskCount"
                  :key="taskIndex"
                  class="p-3 bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl flex flex-col gap-3 shadow-sm"
                >
                  <div class="h-5 w-3/4 bg-n-slate-3 rounded" />
                  <div class="flex justify-between items-center">
                    <div class="h-4 w-16 bg-n-slate-3 rounded" />
                    <div class="h-6 w-6 rounded-full bg-n-slate-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <KanbanBoard
            v-else
            :steps="steps"
            :tasks-by-step="filteredTasksByStep"
            :collapsed-step-ids="collapsedStepIds"
            :is-drag-enabled="isDragEnabled"
            :step-loading-map="stepLoadingMap"
            :step-meta-map="stepMetaMap"
            :step-fetched-map="stepFetchedMap"
            :is-counts-loading="isCountsLoading"
            :currency="activeBoard?.currency || 'USD'"
            @add-task="openCreateModal"
            @edit-task="openEditModal"
            @duplicate-task="openDuplicateModal"
            @delete-task="openDeleteDialog"
            @edit-step="openStepModal"
            @add-step="openCreateStepModal"
            @update-task="saveTask"
            @move-task="moveTask"
            @enable-filter="onEnableFilter"
            @load-more="onLoadMore"
            @expand-step="onExpandStep"
          />
        </div>
      </div>
    </div>
    <KanbanTaskModal
      v-if="showTaskModal"
      :show="showTaskModal"
      :task="selectedTask"
      :duplicate-task="duplicateTask"
      :step-id="selectedStepId"
      :steps="steps"
      :board-name="activeBoardName"
      :board-id="activeBoard?.id"
      :is-saving="isSaving"
      :is-deleting="isDeleting"
      :board-agents="activeBoard?.assigned_agents || []"
      :currency="activeBoard?.currency || 'USD'"
      @close="closeModal"
      @save="saveTask"
      @delete="deleteTask"
    />
    <KanbanStepModal
      v-if="showStepModal"
      :show="showStepModal"
      :step="selectedStep"
      :board-name="activeBoardName"
      :is-saving="isSavingStep"
      :is-deleting="isDeletingStep"
      :can-delete="isStepDeletable"
      @close="closeStepModal"
      @save="saveStep"
      @delete="deleteStep"
    />
    <KanbanDeleteTaskDialog
      :show="showDeleteDialog"
      :task-title="taskToDelete?.title"
      :is-deleting="isDeleting"
      @confirm="confirmDeleteTask"
      @close="closeDeleteDialog"
    />
    <KanbanBoardModal
      v-if="showBoardModal"
      :show="showBoardModal"
      :is-saving="isSavingBoard"
      @close="closeBoardModal"
      @save="saveBoard"
    />
  </div>
</template>

<style scoped>
:deep(main) {
  @apply flex-1 flex flex-col min-h-0;
}
</style>
